import { logError } from '@cp/src/utils/ApiHelper';
import { TabSharingStatus, WACallStatus, WAStorageState, WA_STORAGE_KEY } from './interface';
import { HEARTBEAT_STALE_MS, isBlockingStatus, getTabId } from './utils';

export const readStoredState = (): WAStorageState | null => {
  try {
    const raw = localStorage.getItem(WA_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

export const clearStoredState = (): void => {
  try {
    localStorage.removeItem(WA_STORAGE_KEY);
  } catch {
    logError('Failed to clear stored state');
  }
};

export const isStoredStateStale = (stored: WAStorageState): boolean => {
  if (!stored.lastUpdated) return false;
  return Date.now() - stored.lastUpdated > HEARTBEAT_STALE_MS;
};

export interface WriteStoredStateParams {
  status: WACallStatus;
  error: string;
  isCallCompleted: boolean;
  pendingPhone: string;
  active: boolean;
  loanId: string;
  customerId: string;
  tabSharingStatus: TabSharingStatus;
}

/**
 * Persists call state to localStorage. Writes only when this tab owns the state, or when there is no active blocking state
 * owned by another tab. This prevents viewer tabs from overwriting the owner tab's state.
 */
export const writeStoredState = (params: WriteStoredStateParams): void => {
  try {
    const existing = readStoredState();
    // Non-owner cannot overwrite another tab's active call
    if (
      existing &&
      existing.ownerTabId !== getTabId() &&
      isBlockingStatus(existing.status) &&
      !isStoredStateStale(existing)
    ) {
      return;
    }

    // Owner navigated to a different customer — don't overwrite the active call entry with a non-blocking (idle/error) write for a different context.
    if (
      existing &&
      existing.ownerTabId === getTabId() &&
      isBlockingStatus(existing.status) &&
      !isStoredStateStale(existing) &&
      (existing.loanId !== params.loanId || existing.customerId !== params.customerId) &&
      !isBlockingStatus(params.status)
    ) {
      return;
    }

    const entry: WAStorageState = {
      ...params,
      ownerTabId: getTabId(),
      lastUpdated: Date.now()
    };
    localStorage.setItem(WA_STORAGE_KEY, JSON.stringify(entry));
  } catch {
    logError('Failed to write stored state');
  }
};

// Bumps the heartbeat timestamp for the owner tab so non-owner tabs detect the session is alive.
export const updateHeartbeat = (): void => {
  try {
    const current = readStoredState();
    if (current?.ownerTabId !== getTabId()) return;
    current.lastUpdated = Date.now();
    localStorage.setItem(WA_STORAGE_KEY, JSON.stringify(current));
  } catch {
    logError('Failed to update heartbeat');
    // console.log(['WA Web Call Debug', 'Failed to update heartbeat'])
  }
};
